import { Button } from "@aurumiq/ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import {
  type ColumnDef,
  createColumnHelper,
  DataTable,
  type PaginationState,
} from "@aurumiq/ui/composite/data-table";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { PackagePlus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { ArtifactSummary } from "@/features/releases/artifacts";
import { CiHelpCard } from "@/features/releases/ci-help-card";
import { useReleases } from "@/features/releases/queries";
import { ReleaseActions } from "@/features/releases/release-actions";
import { DraftReleaseDialog, EditReleaseDialog } from "@/features/releases/release-dialogs";
import { errorMessage } from "@/lib/api";
import type { Release, ReleaseChannel } from "@/lib/types";

const col = createColumnHelper<Release>();

export function ReleasesPage() {
  const navigate = useNavigate();
  const canWrite = usePermission("releases:write");
  const [sp, setSp] = useSearchParams();
  const channel = (sp.get("channel") === "beta" ? "beta" : "stable") as ReleaseChannel;
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const releases = useReleases(channel, pagination.pageIndex + 1);
  const [draftOpen, setDraftOpen] = useState(false);
  const [editing, setEditing] = useState<Release | null>(null);

  const columns = useMemo(
    () =>
      [
        col.accessor("version", {
          header: "Version",
          cell: (c) => (
            <Link
              to={`/releases/${c.getValue()}`}
              className="font-mono font-semibold text-primary hover:underline"
            >
              {c.getValue()}
            </Link>
          ),
        }),
        col.accessor("status", {
          header: "Status",
          cell: (c) => (
            <StatusTag variant={statusVariant(c.getValue())}>
              {c.getValue().toUpperCase()}
            </StatusTag>
          ),
        }),
        col.accessor("published_at", {
          header: "Published",
          cell: (c) => <span title={dmyTime(c.getValue())}>{relativeTime(c.getValue())}</span>,
        }),
        col.accessor("staging_pct", {
          header: "Staging",
          meta: { numeric: true },
          cell: (c) => `${c.getValue()}%`,
        }),
        col.accessor("artifacts", {
          header: "Artifacts",
          enableSorting: false,
          cell: (c) => <ArtifactSummary artifacts={c.getValue()} />,
        }),
        col.display({
          id: "actions",
          header: "",
          meta: { numeric: true },
          cell: (c) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: stops the row-click navigation from firing under the menu
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <ReleaseActions release={c.row.original} onEdit={setEditing} />
            </span>
          ),
        }),
      ] as ColumnDef<Release, unknown>[],
    [],
  );
  const total = releases.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Releases"
        description="Desktop app builds per channel. Publish gates what the auto-updater serves."
        actions={
          canWrite ? (
            <Button variant="gold" onClick={() => setDraftOpen(true)}>
              <PackagePlus /> Draft release
            </Button>
          ) : null
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <DataTable
          columns={columns}
          data={releases.data?.items ?? []}
          isLoading={releases.isPending}
          getRowId={(r) => r.id}
          pageCount={Math.max(1, Math.ceil(total / pagination.pageSize))}
          pagination={pagination}
          onPaginationChange={setPagination}
          onRowClick={(row) => navigate(`/releases/${row.original.version}`)}
          emptyMessage={
            releases.isError
              ? `Could not load releases: ${errorMessage(releases.error)}`
              : `No ${channel} releases yet.`
          }
          toolbar={
            <Tabs
              value={channel}
              onValueChange={(v) => {
                setSp(v === "stable" ? {} : { channel: v });
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
            >
              <TabsList>
                <TabsTrigger value="stable">Stable</TabsTrigger>
                <TabsTrigger value="beta">Beta</TabsTrigger>
              </TabsList>
            </Tabs>
          }
        />
        <CiHelpCard channel={channel} />
      </div>
      <DraftReleaseDialog
        open={draftOpen}
        onOpenChange={setDraftOpen}
        channel={channel}
        onCreated={(r) => navigate(`/releases/${r.version}`)}
      />
      <EditReleaseDialog release={editing} onOpenChange={(o) => !o && setEditing(null)} />
    </>
  );
}
